import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Download,
  Upload,
  HardDrive,
  Clock,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Database,
  RefreshCw,
} from 'lucide-react';

interface SystemStats {
  activeUsers: number;
  toolDefinitions: number;
  toolUnits: number;
  totalCheckouts: number;
  totalJobs: number;
  toolrooms: number;
}

interface ExportRecord {
  id: string;
  type: string;
  requested_by: string;
  requested_by_name: string;
  status: 'processing' | 'completed' | 'failed';
  created_at: string;
  completed_at?: string;
}

interface BackupData {
  exportId: string;
  backup: Record<string, unknown[]>;
  exportedAt: string;
}

interface RestoreResult {
  restored: number;
  errors: string[];
}

export function Backup() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [restoreData, setRestoreData] = useState<Record<string, unknown[]> | null>(null);
  const [restoreError, setRestoreError] = useState('');
  const [restoreResult, setRestoreResult] = useState<RestoreResult | null>(null);

  // System stats
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const res = await api.get<SystemStats>('/admin/stats');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Export history
  const { data: exports, isLoading: loadingExports, refetch: refetchExports } = useQuery({
    queryKey: ['admin-exports'],
    queryFn: async () => {
      const res = await api.get<ExportRecord[]>('/admin/exports');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Create backup
  const backupMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<BackupData>('/admin/backup', {});
      if (!res.success) throw new Error(res.error || 'Failed to create backup');
      return res.data;
    },
    onSuccess: (data) => {
      // Download the backup as JSON
      if (data?.backup) {
        const blob = new Blob([JSON.stringify(data.backup, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tool-crib-backup-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
      refetchExports();
    },
  });

  // Restore from backup
  const restoreMutation = useMutation({
    mutationFn: async (backup: Record<string, unknown[]>) => {
      const res = await api.post<RestoreResult>('/admin/restore', {
        backup,
        options: { clearExisting: false },
      });
      if (!res.success) throw new Error(res.error || 'Failed to restore');
      return res.data;
    },
    onSuccess: (result) => {
      setRestoreResult(result || { restored: 0, errors: [] });
      setRestoreData(null);
      queryClient.invalidateQueries();
    },
    onError: (err) => {
      setRestoreError(err instanceof Error ? err.message : 'Failed to restore');
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setRestoreError('');
    setRestoreResult(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (typeof data !== 'object' || Array.isArray(data)) {
        setRestoreError('Invalid backup file format');
        return;
      }

      setRestoreData(data);
    } catch {
      setRestoreError('Failed to parse backup file');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'processing':
        return <Badge variant="warning"><Clock className="h-3 w-3 mr-1" />Processing</Badge>;
      case 'failed':
        return <Badge variant="error"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Backup & Restore</h1>
          <p className="text-foreground-muted">Manage database backups</p>
        </div>
      </div>

      {/* System Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Overview
          </CardTitle>
        </CardHeader>
        <CardBody>
          {loadingStats ? (
            <div className="text-center py-8">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-4 bg-surface-hover rounded-lg">
                <p className="text-2xl font-bold">{stats?.activeUsers || 0}</p>
                <p className="text-sm text-foreground-muted">Users</p>
              </div>
              <div className="text-center p-4 bg-surface-hover rounded-lg">
                <p className="text-2xl font-bold">{stats?.toolDefinitions || 0}</p>
                <p className="text-sm text-foreground-muted">Tool Types</p>
              </div>
              <div className="text-center p-4 bg-surface-hover rounded-lg">
                <p className="text-2xl font-bold">{stats?.toolUnits || 0}</p>
                <p className="text-sm text-foreground-muted">Tool Units</p>
              </div>
              <div className="text-center p-4 bg-surface-hover rounded-lg">
                <p className="text-2xl font-bold">{stats?.totalCheckouts || 0}</p>
                <p className="text-sm text-foreground-muted">Checkouts</p>
              </div>
              <div className="text-center p-4 bg-surface-hover rounded-lg">
                <p className="text-2xl font-bold">{stats?.totalJobs || 0}</p>
                <p className="text-sm text-foreground-muted">Jobs</p>
              </div>
              <div className="text-center p-4 bg-surface-hover rounded-lg">
                <p className="text-2xl font-bold">{stats?.toolrooms || 0}</p>
                <p className="text-sm text-foreground-muted">Toolrooms</p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Backup Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Create Backup
            </CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-foreground-muted mb-4">
              Download a complete backup of your Tool Crib database. This includes all tools,
              checkouts, users, and configuration data.
            </p>
            <Button
              onClick={() => backupMutation.mutate()}
              loading={backupMutation.isPending}
              className="w-full"
            >
              <HardDrive className="h-4 w-4" />
              Download Backup
            </Button>
          </CardBody>
        </Card>

        {/* Restore */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Restore from Backup
            </CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-foreground-muted mb-4">
              Restore data from a previously downloaded backup file. Existing data will be
              preserved unless it conflicts with restored data.
            </p>
            <input
              type="file"
              ref={fileInputRef}
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4" />
              Select Backup File
            </Button>
          </CardBody>
        </Card>
      </div>

      {/* Restore Error */}
      {restoreError && (
        <Card>
          <CardBody>
            <div className="flex items-center gap-3 text-error">
              <XCircle className="h-5 w-5 flex-shrink-0" />
              <p>{restoreError}</p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Restore Result */}
      {restoreResult && (
        <Card>
          <CardBody>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {restoreResult.errors.length === 0 ? (
                  <CheckCircle className="h-6 w-6 text-success" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-warning" />
                )}
                <div>
                  <p className="font-medium">
                    Restored {restoreResult.restored} record(s)
                  </p>
                  {restoreResult.errors.length > 0 && (
                    <p className="text-sm text-foreground-muted">
                      {restoreResult.errors.length} error(s)
                    </p>
                  )}
                </div>
              </div>

              {restoreResult.errors.length > 0 && (
                <div className="p-4 bg-error/10 rounded-lg max-h-48 overflow-y-auto">
                  <p className="font-medium text-error mb-2">Errors:</p>
                  <ul className="text-sm space-y-1">
                    {restoreResult.errors.slice(0, 20).map((err, idx) => (
                      <li key={idx} className="text-foreground-muted">{err}</li>
                    ))}
                    {restoreResult.errors.length > 20 && (
                      <li className="text-foreground-muted">
                        ... and {restoreResult.errors.length - 20} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Restore Preview */}
      {restoreData && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Restore Preview</CardTitle>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setRestoreData(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => restoreMutation.mutate(restoreData)}
                loading={restoreMutation.isPending}
              >
                <RefreshCw className="h-4 w-4" />
                Restore Data
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-warning">Caution</p>
                  <p className="text-sm text-foreground-muted mt-1">
                    Restoring data may overwrite existing records. Make sure you have a
                    current backup before proceeding.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="font-medium">Tables to restore:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(restoreData).map(([table, records]) => (
                  <div key={table} className="p-3 bg-surface-hover rounded-lg">
                    <p className="font-medium text-sm">{table}</p>
                    <p className="text-xs text-foreground-muted">
                      {(records as unknown[]).length} records
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Export History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Backup History
          </CardTitle>
        </CardHeader>
        <CardBody>
          {loadingExports ? (
            <div className="text-center py-8">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : exports?.length === 0 ? (
            <div className="text-center py-8 text-foreground-muted">
              <HardDrive className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No backup history</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Requested By</th>
                    <th>Created</th>
                    <th>Completed</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {exports?.map((exp) => (
                    <tr key={exp.id}>
                      <td className="font-medium">{exp.type.replace('_', ' ')}</td>
                      <td>
                        <span className="flex items-center gap-2 text-foreground-muted">
                          <User className="h-4 w-4" />
                          {exp.requested_by_name}
                        </span>
                      </td>
                      <td className="text-foreground-muted text-sm">
                        {new Date(exp.created_at).toLocaleString()}
                      </td>
                      <td className="text-foreground-muted text-sm">
                        {exp.completed_at
                          ? new Date(exp.completed_at).toLocaleString()
                          : '-'}
                      </td>
                      <td>{getStatusBadge(exp.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
